import {
  MessageStatusEnum,
  UpsertMessage,
  useGetOneMessageQuery,
  useGetUploadSignedUrlMessageMutation,
  useInitMessageMutation,
  usePatchMessageMutation,
  usePublishMessageMutation
} from 'api/MessagesApi';
import axios, { AxiosProgressEvent } from 'axios';
import {
  detectMessageMediaConstraintKey,
  getMessageMediaConstraint,
  validateMessageMediaFile
} from 'constants/mediaConstraints';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { selectAuthUser } from 'store/authSlice';
import { securityLogger } from 'utils/securityLogger';

export const useMessageService = (initMessageId?: string) => {
  const { messageId: messageIdFromParams } = useParams();
  const messageIdFromRoute = messageIdFromParams === 'new' ? undefined : messageIdFromParams;
  const messageId = initMessageId || messageIdFromRoute;
  const authUser = useSelector(selectAuthUser);

  const [uploadIsLoading, setUploadIsLoading] = useState(false);
  const [progressEvent, setProgressEvent] = useState<AxiosProgressEvent | null>(null);
  const [axiosController, setAxiosController] = useState(() => new AbortController());

  const [initMessageMutation, { isLoading: initMessageLoading }] = useInitMessageMutation();
  const [patchMessageMutation, { isLoading: patchMessageLoading }] = usePatchMessageMutation();
  const [publishMessageMutation, { isLoading: publishMessageLoading }] = usePublishMessageMutation();
  const [getUploadSignedUrlMutation] = useGetUploadSignedUrlMessageMutation();

  const { data: messageResponse, isLoading: messageLoading } = useGetOneMessageQuery(
    { id: messageId! },
    { skip: !messageId }
  );

  const updateMessageRecipients = async (data: Pick<UpsertMessage, 'type' | 'contactIds'>) => {
    const patientIdentityPayload = authUser?.patientProfileId ? { patientProfileId: authUser.patientProfileId } : {};

    try {
      if (messageId) {
        return await patchMessageMutation({ id: messageId, ...data }).unwrap();
      } else {
        return await initMessageMutation({ ...data, ...patientIdentityPayload }).unwrap();
      }
    } catch (e) {
      securityLogger.error('Failed to update message recipients', e);
    }
  };

  const updateMessageEvent = async (
    data: Pick<UpsertMessage, 'eventType' | 'eventHint' | 'deliveryYear' | 'deliveryMonth' | 'deliveryDay'>
  ) => _updateData(data);

  const updateMessageContent = async (data: Partial<Pick<UpsertMessage, 'content' | 'name' | 'fileName'>>) =>
    _updateData({ ...data });

  const updateMessageName = async (data: Pick<UpsertMessage, 'name'>) => _updateData(data);

  const updateMessageAiFields = async (
    data: Partial<Pick<UpsertMessage, 'aiTones' | 'aiFocus' | 'aiMemory' | 'aiClosing' | 'aiOtherInfo'>>
  ) => _updateData(data);

  const _updateData = async (data: Partial<UpsertMessage>) => {
    if (!messageId) {
      securityLogger.error('Message ID is required to update event recipients');
      return null;
    }

    try {
      return patchMessageMutation({ ...data, id: messageId }).unwrap();
    } catch (e) {
      securityLogger.error('Failed to update message data', e);
    }
  };

  const uploadFile = async (file: File | null) => {
    if (!messageId) {
      securityLogger.error('Message ID is required to upload media file');
      return null;
    }

    setUploadIsLoading(true);
    try {
      if (file) {
        const audioBlob = file.type.startsWith('audio/') ? file : null;

        if (audioBlob && audioBlob.size < 1024) {
          securityLogger.warn('File rejected before upload because audio file was empty or incomplete', {
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size
          });
          return null;
        }

        const fileSizeBytes = file.size;
        const rawContentType = file.type || 'application/octet-stream';
        const contentType = rawContentType.split(';')[0].trim();

        securityLogger.info('Message media upload request', {
          rawContentType: file.type,
          contentType,
          fileSizeBytes,
          fileSizeBytesType: typeof fileSizeBytes,
          fileName: file.name,
          fileSize: file.size
        });

        const constraintKey = detectMessageMediaConstraintKey(file);
        if (!constraintKey) {
          securityLogger.warn('File rejected before signed URL request due to unsupported media type', {
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size
          });
          return null;
        }

        const preSignedUrlValidation = await validateMessageMediaFile(file, constraintKey);
        if (!preSignedUrlValidation.isValid) {
          securityLogger.warn('File rejected before signed URL request due to media constraints', {
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
            maxFileSize: getMessageMediaConstraint(constraintKey).maxFileSize,
            validationError: preSignedUrlValidation.errorCode
          });
          return null;
        }

        const url = await getUploadSignedUrlMutation({
          id: messageId,
          fileName: file.name,
          contentType,
          fileSizeBytes
        }).unwrap();

        const preUploadValidation = await validateMessageMediaFile(file, constraintKey);
        if (!preUploadValidation.isValid) {
          securityLogger.warn('File rejected before PUT upload due to media constraints', {
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
            maxFileSize: getMessageMediaConstraint(constraintKey).maxFileSize,
            validationError: preUploadValidation.errorCode
          });
          return null;
        }

        const result = await axios.put(url, file, {
          headers: {
            'Content-Type': contentType
          },
          signal: axiosController.signal,
          onUploadProgress: (upload) => {
            setProgressEvent(upload.progress !== 1 ? upload : null);
          }
        });

        if (result.status === 200) {
          return _updateData({ fileName: file.name });
        }
      } else {
        return _updateData({ fileName: '' });
      }
    } catch (e) {
      securityLogger.error('Failed to upload message media file', e);
    } finally {
      setUploadIsLoading(false);
    }
    return null;
  };

  const abortUploading = () => {
    axiosController.abort();
    setProgressEvent(null);
    setAxiosController(new AbortController());
  };

  const publishMessage = async (name?: string) => {
    if (!messageResponse) {
      throw new Error('Message is not available.');
    }
    if (messageResponse.status === MessageStatusEnum.Published) {
      throw new Error('Message is already published');
    }

    try {
      return await publishMessageMutation({ id: messageResponse.id, name }).unwrap();
    } catch (e) {
      securityLogger.error('Failed to publish message', e);
    }
  };

  return {
    message: messageResponse,
    messageIsLoading: messageLoading,
    updateIsLoading: initMessageLoading || patchMessageLoading || publishMessageLoading,
    uploadIsLoading,
    progressEvent,
    updateMessageRecipients,
    updateMessageEvent,
    updateMessageContent,
    updateMessageName,
    updateMessageAiFields,
    uploadFile,
    abortUploading,
    publishMessage
  };
};
